import React, { useRef, useState, useEffect } from 'react';
import { NODES } from '@/constants/locations';
import { motion, useInView, useMotionValue, animate } from 'framer-motion';
import { useIntelReports } from '@/lib/intelSourceV2';
import { shouldSkipAnimation } from '@/lib/prerender';

/**
 * AnimatedCounter — renders the FINAL value by default (what crawlers, no-JS
 * clients, prerender snapshots and reduced-motion users see), then — only when
 * JS is running, motion is allowed and the element scrolls into view — briefly
 * counts up from 0 as a progressive enhancement. It must never leave a "0" on
 * screen: the section is the homepage's trust/proof strip.
 */
const AnimatedCounter = ({ to, prefix = '', suffix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const count = useMotionValue(to);
  const [display, setDisplay] = useState(to);

  useEffect(() => {
    if (!inView || shouldSkipAnimation()) {
      setDisplay(to);
      return undefined;
    }
    count.set(0);
    const controls = animate(count, to, {
      duration: 1.8,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(Math.round(v)),
      onComplete: () => setDisplay(to),
    });
    return () => controls.stop();
  }, [inView, count, to]);

  // Keep in sync if the target changes after data loads (e.g. case-study count).
  useEffect(() => {
    if (!inView) setDisplay(to);
  }, [to, inView]);

  return (
    <span ref={ref} data-testid="metric-counter" data-final={`${prefix}${to}${suffix}`}>
      {prefix}{display}{suffix}
    </span>
  );
};

// Static claims. "Case studies published" is derived live from the Intelligence
// Sheet (case_study rows) so it can never drift from the real catalogue; the
// fallback matches the bundled legacy dataset.
const FALLBACK_CASE_STUDY_COUNT = 20;

const buildMetrics = (caseStudyCount) => [
  // Brand guide: no hard dollar figure for assets under management — use a verifiable footprint figure instead.
  { to: NODES.length, prefix: '', suffix: '', label: 'Global Delivery\nNodes', accent: '#4A90E2' },
  { to: caseStudyCount, prefix: '', suffix: '', label: 'Case Studies\nPublished', accent: '#9e591e' },
  { to: 8, prefix: '', suffix: '+', label: 'Years Enterprise\nDelivery', accent: '#4A90E2' },
  { to: 3, prefix: '', suffix: '', label: 'Specialist\nService Tracks', accent: '#9e591e' },
];

export default function MetricsStrip() {
  const { reports } = useIntelReports();
  const liveCount = Array.isArray(reports)
    ? reports.filter((r) => r.type === 'case_study').length
    : 0;
  const metrics = buildMetrics(liveCount > 0 ? liveCount : FALLBACK_CASE_STUDY_COUNT);
  const skip = shouldSkipAnimation();

  return (
    <section
      className="relative py-14 md:py-16 bg-[#1A1D21] overflow-hidden border-b border-[#2D323E]"
      aria-label="Key metrics"
      data-testid="metrics-strip"
    >
      {/* Subtle dual-tone gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#4A90E2]/5 via-transparent to-[#9e591e]/5 pointer-events-none" />

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial={skip ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.65, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="group"
            >
              {/* Glassmorphic card */}
              <div className="relative bg-white/[0.04] backdrop-blur-md border border-white/[0.08] rounded-[2px] p-6 md:p-8 overflow-hidden hover:border-white/20 hover:bg-white/[0.07] transition-all duration-300 cursor-default">
                <div
                  className="absolute top-0 left-0 right-0 h-[2px] opacity-50 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: m.accent }}
                />
                <div
                  className="absolute bottom-[-24px] right-[-24px] w-28 h-28 rounded-full blur-2xl opacity-[0.08] group-hover:opacity-[0.18] transition-opacity duration-300"
                  style={{ background: m.accent }}
                />

                <div
                  className="font-title text-4xl md:text-5xl leading-none mb-3 relative z-10"
                  style={{ color: m.accent }}
                >
                  <AnimatedCounter to={m.to} prefix={m.prefix} suffix={m.suffix} />
                </div>

                <p className="font-mono text-[10px] text-gray-400 uppercase tracking-[0.15em] leading-relaxed whitespace-pre-line relative z-10">
                  {m.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
